// TextField.tsx
import React, { InputHTMLAttributes, Ref, useState } from 'react'
import styled from 'styled-components'
import { Icon } from '@ecommerce/chile-customer-webapp/src/components/Icon/Icon'
import { Status, Wrapper, ControlSection, Label as OriginalLabel } from './Styled'
import { baseInput } from '../../../styles/baseForms'
import Anchor from '../../atoms/Anchor'
import InfoMessage from '../../atoms/InfoMessage'
import SuccessIcon from '../../atoms/Icons/SuccessIcon'
import LoadingIcon from '../../atoms/Icons/LoadingIcon'
import EyesOpenIcon from '../../atoms/Icons/EyesOpenIcon'
import EyesCloseIcon from '../../atoms/Icons/EyesCloseIcon'
import FieldTooltip from './FieldTooltip' // 👈 Importa tu FieldTooltip autocontenido

const InputStyled = styled.input<{ status: Status | undefined }>`
  ${({ status }) => {
    return baseInput(status)
  }}
`

const LabelWithTooltip = styled(OriginalLabel)`
  display: flex;
  align-items: center;
`

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  helpMessage?: string
  errorMessage?: string
  successMessage?: string
  loading?: boolean
  className?: string
  status?: Status
  tooltipText?: string
  tooltipTipPosition?: 'left' | 'right' | 'top' | 'bottom'
  tooltipClassName?: string
}

const TextField = React.forwardRef((props: TextFieldProps, ref: Ref<HTMLInputElement>) => {
  const {
    label,
    placeholder,
    name,
    className,
    errorMessage,
    helpMessage,
    successMessage,
    status,
    type,
    loading,
    tooltipText,
    tooltipTipPosition,
    tooltipClassName,
    ...inputProps
  } = props

  const [isFocus, setIsFocus] = useState(false)
  const [showText, setShowText] = useState(type !== 'password')
  // Ya no se necesita el estado 'isTooltipVisible' aquí

  const getMessage = () => {
    if (status === 'error') return errorMessage
    if (status === 'success') return successMessage
    return helpMessage
  }

  const getStatusIcon = () => {
    if (status === 'success') return <SuccessIcon />
    if (status === 'error') return <Icon iconId="error_outline" />
    return null
  }

  return (
    <Wrapper className={`${className || ''} status--${status}`} status={status}>
      {label && (
        <LabelWithTooltip className="Select__label" htmlFor={name}>
          <span>{label}</span>
          {tooltipText && (
            <FieldTooltip
              tipPosition={tooltipTipPosition || 'left'}
              className={tooltipClassName}
              content={tooltipText}
              marginLeft="8px"
            >
              <Icon iconId="info_fill" size={15} color="#5F6F86" />
            </FieldTooltip>
          )}
        </LabelWithTooltip>
      )}
      <ControlSection>
        <InputStyled
          {...inputProps}
          data-test={`input-${name}`}
          ref={ref}
          type={showText && type === 'password' ? 'text' : type}
          placeholder={placeholder || (isFocus ? '' : label)}
          onFocus={() => setIsFocus(true)}
          onBlur={(e) => {
            if (inputProps.onBlur) {
              inputProps.onBlur(e)
            }
            setIsFocus(false)
          }}
          name={name}
          status={status}
          className="TextFieldComponent__Base" // Clase para el input específico
        />
        {type === 'password' && (
          <Anchor
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              setShowText(!showText)
            }}
          >
            {showText ? <EyesOpenIcon /> : <EyesCloseIcon />}
          </Anchor>
        )}
        {loading && (
          <div className="loading">
            <LoadingIcon />
          </div>
        )}
      </ControlSection>
      <InfoMessage
        className={`TextField__help status--${status}`}
        isHidden={!(errorMessage || helpMessage || successMessage)}
        message={getMessage()}
        icon={getStatusIcon()}
      />
    </Wrapper>
  )
})

export default TextField
